<template>
  <div class="card-body table mb-0">

    <!-- HEADER -->
    <PageHeader v-if="showHeading" title="Payment Methods" class="poppins fw-normal text-nowrap mt-2" />

    <!-- TABLE -->
    <GeneralTable :isProcessing="processing" :items="items" :fields="fields" :showEyeButton="true"
      :showActionButton="true" :showEditButton="false" @showItem="openModal" @deleteItem="deletePaymentMethod"
      @makeDefault="makeDefault">
    </GeneralTable>

    <!-- DETAILS MODAL -->
    <b-modal ref="paymentMethodModal" hide-footer size="lg" title="Payment Method Details">
      <div v-if="selectedMethod" class="pm-info">

        <div class="pm-column">
          <p><strong>ID:</strong> {{ selectedMethod.id }}</p>
          <p><strong>Type:</strong> {{ selectedMethod.type }}</p>
          <p><strong>Default:</strong>
            <span class="badge" :class="selectedMethod.is_default ? 'badge-success' : 'badge-secondary'">
              {{ selectedMethod.is_default ? 'Yes' : 'No' }}
            </span>
          </p>
        </div>

        <div class="pm-column">
          <p><strong>Brand:</strong> {{ card.brand || '—' }}</p>
          <p><strong>Last 4:</strong> **** {{ card.last4 || '—' }}</p>
          <p><strong>Expiry:</strong> {{ card.exp_month }}/{{ card.exp_year }}</p>
          <p><strong>Funding:</strong> {{ card.funding || '—' }}</p>
        </div>

        <div class="pm-column full-width">
          <h6>Billing Details</h6>
          <p><strong>Name:</strong> {{ selectedMethod.billing_details?.name || '—' }}</p>
          <p><strong>Email:</strong> {{ selectedMethod.billing_details?.email || '—' }}</p>
        </div>

        <div class="pm-column full-width">
          <p><strong>Created:</strong> {{ formatDate(selectedMethod.created) }}</p>
        </div>

      </div>
    </b-modal>

  </div>
</template>

<script>
import GeneralTable from "@/components/MagicTable/GeneralTable.vue";
import PageHeader from "@/components/static/page-header";
import Swal from "sweetalert2";

export default {
  name: "UserPaymentMethods",

  components: { GeneralTable, PageHeader },

  props: {
    user: { type: Object, required: true },
    showHeading: { type: Boolean, default: true }
  },

  data() {
    return {
      processing: false,
      items: [],
      selectedMethod: null,

      fields: [
        { key: "default_badge", label: "Status" },
        { key: "card_brand", label: "Brand" },
        { key: "card_last4", label: "Last 4" },
        { key: "expiry", label: "Expiry" },
        { key: "created_at", label: "Added On" },
        { key: "make_default" },
        { key: "action" }
      ]
    };
  },

  computed: {
    card() {
      return this.selectedMethod?.card || {};
    }
  },

  mounted() {
    this.fetchPaymentMethods();
  },

  methods: {
    deletePaymentMethod(id) {
      try {
        Swal.fire({
          title: "Are you sure you want to delete this payment method?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, Delete it!",
        }).then(async (result) => {
          if (result.isConfirmed) {
            let url = '/payment-methods/delete';
            this.$axios.post(url, { user_id: this.user.id, payment_method_id: id }).then((response) => {
              this.triggerSwal("Payment Method deleted Successfully", "success");
              this.fetchPaymentMethods();
            }).catch((error) => {
              this.handleErrorResponse(error.response, "error");
            }).finally(() => {
              this.processing = false;
            });

          }

        });
      } catch (error) {
        this.handleErrorResponse(error.response, "error");
        this.processing = false;
      }
    },
    makeDefault(id) {
      try {
        Swal.fire({
          title: "Are you sure you want to make this default?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, Make it Default!",
        }).then(async (result) => {
          if (result.isConfirmed) {
            let url = '/payment-methods/set-default';
            this.$axios.post(url, { user_id: this.user.id, payment_method_id: id }).then((response) => {
              this.triggerSwal("Payment Method Set as Default Successfully", "success");
              this.fetchPaymentMethods();
            }).catch((error) => {
              this.handleErrorResponse(error.response, "error");
            }).finally(() => {
            });

          }else {
            this.fetchPaymentMethods();
          }

        });
      } catch (error) {
        this.handleErrorResponse(error.response, "error");
        this.processing = false;
      }
    },
    fetchPaymentMethods() {
      this.processing = true;

      this.$axios
        .get("payments/current-user-payment-methods", {
          params: { user_id: this.user.id }
        })
        .then(res => {
          const methods = res?.data?.data?.payment_methods || [];

          this.items = methods.map(pm => ({
            id: pm.id,
            card_brand: pm.card?.brand || '—',
            card_last4: pm.card?.last4 || '—',
            expiry: pm.card ? `${pm.card.exp_month}/${pm.card.exp_year}` : '—',
            created_at: this.formatDate(pm.created),
            is_default: pm.is_default,
            default_badge: pm.is_default
              ? '🟢 Default'
              : '—',

            _raw: pm
          }));

          this.processing = false;
        })
        .catch(() => {
          this.processing = false;
        });
    },

    openModal(id) {
      const row = this.items.find(i => i.id === id);
      this.selectedMethod = row?._raw || null;
      this.$refs.paymentMethodModal.show();
    },

    formatDate(timestamp) {
      if (!timestamp) return '—';
      return new Date(timestamp * 1000).toLocaleString();
    }
  }
};
</script>

<style scoped>
.pm-info {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.pm-column {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 12px;
}

.full-width {
  grid-column: span 2;
}
</style>